// ./src/components/features/AccountEmails/AccountEmailEditModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, TextInput, Textarea } from "@mantine/core";
import { AccountEmail, AccountEmailInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import { STATUSES } from "@sdflc/utils";

export interface AccountEmailEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Account email to edit (if null/undefined, it's create mode) */
  accountEmail?: AccountEmail | null;

  /** Callback when account email should be created */
  onCreateAccountEmail?: (
    data: AccountEmailInput
  ) => Promise<AccountEmail | null>;

  /** Callback when account email should be updated */
  onUpdateAccountEmail?: (
    id: string,
    data: AccountEmailInput
  ) => Promise<AccountEmail | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  name?: string | null | undefined;
  email?: string | null | undefined;
  description?: string | null | undefined;
  status: number;
}

const initialFormData: FormData = {
  name: "",
  email: "",
  description: "",
  status: STATUSES.ACTIVE,
};

export const AccountEmailEditModal: React.FC<AccountEmailEditModalProps> = ({
  opened,
  onClose,
  accountEmail,
  onCreateAccountEmail,
  onUpdateAccountEmail,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(accountEmail?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or accountEmail changes
  useEffect(() => {
    if (opened) {
      if (accountEmail) {
        // Edit mode - populate form with data
        setFormData({
          name: accountEmail.name || "",
          email: accountEmail.email || "",
          description: accountEmail.description || "",
          status: accountEmail.status ?? STATUSES.ACTIVE,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, accountEmail, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Email is required
    if (!formData.email?.trim()) {
      errors.email = "Email address is required";
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email?.trim())) {
        errors.email = "Please enter a valid email address";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: AccountEmailInput = {
        name: formData.name?.trim() || null,
        email: formData.email?.trim() || null,
        description: formData.description?.trim() || null,
        status: formData.status,
      };

      let result: AccountEmail | null = null;

      if (isEditMode && accountEmail?.id && onUpdateAccountEmail) {
        result = await onUpdateAccountEmail(accountEmail.id, submitData);
      } else if (!isEditMode && onCreateAccountEmail) {
        result = await onCreateAccountEmail(submitData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Account email submission error:", error);
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit Account Email" : "Add Account Email"}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Enter recipient name"
          value={formData.name || ""}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={validationErrors.name}
          size="md"
          type="text"
        />

        <TextInput
          label="Email Address"
          placeholder="Enter email address"
          value={formData.email || ""}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={validationErrors.email}
          required
          withAsterisk
          size="md"
          type="email"
          disabled={isEditMode && formData.status != STATUSES.PENDING}
          description={
            isEditMode
              ? "You can update the email address until it is confirmed"
              : "This email will need to be confirmed before it can be used"
          }
        />

        <Textarea
          label="Description"
          placeholder="Enter description (optional)"
          value={formData.description || ""}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          description="A note to help identify this email (e.g., 'Support inbox', 'Sales notifications')"
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          disabled={formData.status == STATUSES.PENDING}
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};
